import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import { Col, Modal, Row } from 'react-bootstrap';
import Card from 'components/inputs/Card';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import Footer from 'components/general/Footer';
import ReactPagination from 'components/general/ReactPagination';
import { toast } from 'react-toastify';
import Button from 'components/inputs/Button';
import TextInput from 'components/inputs/Input';
import { GET_DEALER_ENTITLEMENT, FETCH_DEALER_CREDIT_TRANSACTIONS, SET_ENTITLEMENT_VERIFIED_MAC, SYNC_ENTITLEMENT } from 'actions/dealerEntitlement';

const MAC_SOCKETS = ['evcc_id', 'evcc_id_2', 'evcc_id_3', 'evcc_id_4', 'evcc_id_5'];

const fmtDate = (d) => (d ? moment(d).format('DD/MM/YYYY') : '—');
const fmtDateTime = (d) => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '—');
// Paise matter: 10% of ₹81 is ₹8.10 and must not be rounded away.
const money = (v) => (_.isNil(v) ? '—' : `₹${Number(v).toFixed(2)}`);
const value = (v) => (_.isNil(v) || v === '' ? '—' : v);

const Field = ({ label, children, md = 4 }) => (
  <Col md={md} className="mb-2">
    <div className="ec__field-label">{label}</div>
    <div className="ec__field-value">{children}</div>
  </Col>
);

Field.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node,
  md: PropTypes.number,
};

const Section = ({ title, children }) => (
  <Card>
    <div className="data-report__inner">
      <div className="ec__section-title mb-2">{title}</div>
      <Row>{children}</Row>
    </div>
  </Card>
);

Section.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

/**
 * One sale: everything the dealer sent, the lifecycle, and the credit
 * statement for this vehicle. The movements answer "where did the money go",
 * which is the question the detail view is usually opened to settle.
 */
const EnrollmentDetail = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dealerId, entitlementId } = useParams();
  const [showMacModal, setShowMacModal] = useState(false);
  const [macId, setMacId] = useState('');
  const [savingMac, setSavingMac] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const detail = useSelector((state) => state.dealerEntitlement.entitlementDetail);
  const transactions = useSelector((state) => state.dealerEntitlement.transactions);
  const stats = useSelector((state) => state.dealerEntitlement.transactionStats);
  const isLoading = useSelector((state) => state.dealerEntitlement.isLoading);
  const page = useSelector((state) => state.dealerEntitlement.txPage);
  const limit = useSelector((state) => state.dealerEntitlement.txLimit);
  const totalPages = useSelector((state) => state.dealerEntitlement.txTotalPages);
  const total = useSelector((state) => state.dealerEntitlement.txTotal);

  const getTransactions = useCallback(
    (data = {}) => {
      dispatch({
        type: FETCH_DEALER_CREDIT_TRANSACTIONS.REQUEST,
        payload: { ...data, entitlement: entitlementId },
      });
    },
    [entitlementId]
  );

  useEffect(() => {
    dispatch({ type: GET_DEALER_ENTITLEMENT.REQUEST, payload: entitlementId });
    getTransactions();
  }, [entitlementId]);

  // Money moves on success, so the sale and its statement are both re-read.
  const saveMac = useCallback(() => {
    setSavingMac(true);
    dispatch({
      type: SET_ENTITLEMENT_VERIFIED_MAC.REQUEST,
      payload: { id: entitlementId, evcc_id: _.trim(macId) },
      cb: (res) => {
        setSavingMac(false);
        if (_.get(res, 'status') !== 200) {
          toast.error(_.get(res, 'response.data.message', 'Could not record this MAC ID'));
          return;
        }
        toast.success(
          _.get(res, 'data.activated')
            ? `MAC ID saved. ₹${_.get(res, 'data.amount')} credit released to the customer.`
            : 'MAC ID saved. No credit was released.'
        );
        setShowMacModal(false);
        setMacId('');
        dispatch({ type: GET_DEALER_ENTITLEMENT.REQUEST, payload: entitlementId });
        getTransactions();
      },
    });
  }, [entitlementId, macId]);

  // The MAC landed but the payout or the dealer callback did not, so both retry.
  const runSync = useCallback(() => {
    setSyncing(true);
    dispatch({
      type: SYNC_ENTITLEMENT.REQUEST,
      payload: entitlementId,
      cb: (res) => {
        setSyncing(false);
        if (_.get(res, 'status') !== 200) {
          toast.error(_.get(res, 'response.data.message', 'Could not sync this enrollment'));
          return;
        }
        const data = _.get(res, 'data', {});
        // Named, not hardcoded, so a second programme reads correctly here.
        const dealerName = _.get(detail, 'dealer.name', 'the dealer');
        if (data.activated) {
          toast.success(`₹${data.amount} credit released to the customer`);
        } else if (data.alreadyInSync) {
          toast.success(`Already up to date. Credit released and ${dealerName} informed.`);
        } else if (data.notified) {
          toast.success(`${dealerName} informed. Credit was already released to the customer.`);
        } else {
          toast.warning(`Credit is already released to the customer. Could not inform ${dealerName}, try again later.`);
        }
        dispatch({ type: GET_DEALER_ENTITLEMENT.REQUEST, payload: entitlementId });
        getTransactions();
      },
    });
  }, [entitlementId, detail]);

  const vehicleMacs = _.filter(
    _.map(MAC_SOCKETS, (socket) => ({ socket, mac: _.get(detail, ['vehicle', socket]) })),
    'mac'
  );

  const creditCards = [
    { key: 'balance', label: t('energyCreditTx.outstanding', 'Balance'), value: money(_.get(stats, 'balance.outstanding')) },
    { key: 'released', label: t('energyCreditTx.released', 'Released'), value: money(_.get(stats, 'movement.released')) },
    { key: 'used', label: t('energyCreditTx.used', 'Used'), value: money(_.get(stats, 'movement.used')) },
    { key: 'expired', label: t('energyCreditTx.expired', 'Expired'), value: money(_.get(stats, 'movement.expired')) },
    { key: 'reversed', label: t('energyCreditTx.reversed', 'Reversed'), value: money(_.get(stats, 'movement.reversed')) },
    { key: 'lifetime', label: t('energyCreditTx.lifetime', 'Lifetime'), value: money(_.get(stats, 'balance.lifetime_value')) },
  ];

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={`${t('header.enrolment', 'Enrolment')} - ${_.get(detail, 'rto_no', '')}`} backTo={`/energyCredits/${dealerId}`} />
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
          <div className="page-content-wrapper scrollable">
            <div className="data-report--main energy-credits__main">
              {isLoading && _.isEmpty(detail) ? (
                <Card>
                  <div className="p-4">
                    <Skeleton height={15} count={8} />
                  </div>
                </Card>
              ) : (
                <React.Fragment>
                  <Section title={t('enrollmentDetail.buyer', 'Buyer')}>
                    <Field label={t('enrollmentDetail.name', 'Name')}>{value(_.get(detail, 'buyer.name'))}</Field>
                    <Field label={t('enrollmentDetail.phone', 'Phone')}>{value(_.get(detail, 'buyer.phone'))}</Field>
                    <Field label={t('enrollmentDetail.email', 'Email')}>{value(_.get(detail, 'buyer.email'))}</Field>
                    <Field label={t('enrollmentDetail.dob', 'Date of Birth')}>{fmtDate(_.get(detail, 'buyer.date_of_birth'))}</Field>
                    <Field label={t('enrollmentDetail.gender', 'Gender')}>{value(_.get(detail, 'buyer.gender'))}</Field>
                    <Field label={t('enrollmentDetail.claimedBy', 'ChargeZone Account')}>{value(_.get(detail, 'user.name'))}</Field>
                    <Field label={t('enrollmentDetail.accountSource', 'Account Created By')}>
                      {_.get(detail, 'buyer_account_created_at')
                        ? `${t('enrollmentDetail.createdByDealer', 'Dealer enrolment')} - ${fmtDateTime(_.get(detail, 'buyer_account_created_at'))}`
                        : t('enrollmentDetail.existingAccount', 'Existing ChargeZone account')}
                    </Field>
                    <Field label={t('enrollmentDetail.currentAddress', 'Current Address')} md={6}>
                      {value(_.get(detail, 'buyer.current_address'))}
                    </Field>
                    <Field label={t('enrollmentDetail.permanentAddress', 'Permanent Address')} md={6}>
                      {value(_.get(detail, 'buyer.permanent_address'))}
                    </Field>
                  </Section>

                  <Section title={t('enrollmentDetail.vehicle', 'Vehicle')}>
                    <Field label={t('enrollmentDetail.make', 'Make')}>{value(_.get(detail, 'vehicle_details.make'))}</Field>
                    <Field label={t('enrollmentDetail.model', 'Model')}>{value(_.get(detail, 'vehicle_details.model'))}</Field>
                    <Field label={t('enrollmentDetail.year', 'Year')}>{value(_.get(detail, 'vehicle_details.year_of_manufacturing'))}</Field>
                    <Field label={t('enrollmentDetail.battery', 'Battery Capacity')}>
                      {value(_.get(detail, 'vehicle_details.battery_capacity'))}
                    </Field>
                    <Field label={t('enrollmentDetail.rto', 'Registration No')}>{value(_.get(detail, 'rto_no'))}</Field>
                    <Field label={t('enrollmentDetail.vin', 'VIN')}>{value(_.get(detail, 'vin_num'))}</Field>
                    <Field label={t('enrollmentDetail.registeredOn', 'Registered On')}>
                      {fmtDate(_.get(detail, 'vehicle_details.date_of_registration'))}
                    </Field>
                    {/* The dealer-supplied MAC is a hint; support must not treat it as identity. */}
                    <Field label={t('enrollmentDetail.expectedMac', 'MAC From Dealer (unverified)')}>
                      {value(_.get(detail, 'expected_evcc_id'))}
                    </Field>
                    <Field label={t('enrollmentDetail.verifiedMac', 'MAC Verified At Session')}>{value(_.get(detail, 'verified_evcc_id'))}</Field>
                    {/* A charger that never reported the MAC leaves the sale stuck, so it is set here. */}
                    <Col md={4} className="mb-2">
                      <div className="ec__field-label">{t('enrollmentDetail.recordMac', 'MAC Not Recorded?')}</div>
                      <Button
                        className="ec__mac-btn"
                        disabled={!_.get(detail, 'vehicle')}
                        onClick={() => {
                          setMacId(_.get(detail, 'verified_evcc_id', ''));
                          setShowMacModal(true);
                        }}
                      >
                        {_.get(detail, 'verified_evcc_id')
                          ? t('enrollmentDetail.correctMac', 'Correct MAC ID')
                          : t('enrollmentDetail.setMac', 'Set MAC ID')}
                      </Button>
                    </Col>
                    {/* Credit or the dealer callback can lag behind a MAC, so both are retried here. */}
                    <Col md={4} className="mb-2">
                      <div className="ec__field-label">{t('enrollmentDetail.outOfSync', 'Credit Not Released?')}</div>
                      <Button className="ec__mac-btn" disabled={syncing || !_.get(detail, 'vehicle')} onClick={runSync}>
                        {syncing ? t('enrollmentDetail.syncing', 'Syncing...') : t('enrollmentDetail.sync', 'Sync Now')}
                      </Button>
                    </Col>
                  </Section>

                  <Section title={t('enrollmentDetail.saleAndLifecycle', 'Sale & Lifecycle')}>
                    <Field label={t('enrollmentDetail.branch', 'Branch')}>{value(_.get(detail, 'dealer_branch.name'))}</Field>
                    <Field label={t('enrollmentDetail.branchRaw', 'Branch As Sent')}>{value(_.get(detail, 'branch_name_raw'))}</Field>
                    <Field label={t('enrollmentDetail.city', 'City')}>
                      {value(_.get(detail, 'city.name') || _.get(detail, 'dealer_branch.city_raw'))}
                      {!_.get(detail, 'city') && ` (${t('enrollmentDetail.unmatched', 'not matched to master')})`}
                    </Field>
                    <Field label={t('enrollmentDetail.inductedAt', 'Inducted On')}>{fmtDate(_.get(detail, 'sale.inducted_at'))}</Field>
                    <Field label={t('enrollmentDetail.receivedAt', 'Received On')}>{fmtDate(_.get(detail, 'sale.received_at'))}</Field>
                    <Field label={t('enrollmentDetail.enrolled', 'Enrolled')}>{fmtDate(_.get(detail, 'enrolled_at'))}</Field>
                    <Field label={t('enrollmentDetail.activated', 'Activated')}>{fmtDate(_.get(detail, 'activated_at'))}</Field>
                    <Field label={t('enrollmentDetail.expires', 'Expires')}>{fmtDate(_.get(detail, 'expires_at'))}</Field>
                    <Field label={t('enrollmentDetail.status', 'Status')}>{_.startCase(_.get(detail, 'status', ''))}</Field>
                    <Field label={t('enrollmentDetail.reviewReason', 'Review Reason')}>{value(_.get(detail, 'review_reason'))}</Field>
                    <Field label={t('enrollmentDetail.programme', 'Programme')}>
                      {value(_.get(detail, 'dealer.name'))}
                      {_.get(detail, 'dealer.code') && ` (${_.get(detail, 'dealer.code')})`}
                    </Field>
                  </Section>

                  <Card>
                    <div className="data-report__inner">
                      <div className="ec__section-title mb-2">{t('enrollmentDetail.creditStatement', 'Credit Statement')}</div>
                      <div className="ec__stats">
                        {_.map(creditCards, (card) => (
                          <div className="ec__stat" key={card.key}>
                            <div className="ec__stat-label">{card.label}</div>
                            <div className="ec__stat-value">{isLoading ? <Skeleton height={15} width={70} /> : card.value}</div>
                          </div>
                        ))}
                      </div>
                      <div className="table-responsive mt-2">
                        <table className="record-list-table ec__statement-table">
                          <thead>
                            <tr>
                              <th>{t('energyCreditTx.at', 'Date')}</th>
                              <th>{t('energyCreditTx.type', 'Type')}</th>
                              <th>{t('energyCreditTx.amount', 'Amount')}</th>
                              <th>{t('energyCreditTx.year', 'Year')}</th>
                              <th>{t('energyCreditTx.station', 'Station')}</th>
                              <th>{t('energyCreditTx.booking', 'Booking ID')}</th>
                              <th>{t('energyCreditTx.invoice', 'Invoice No')}</th>
                              <th>{t('energyCreditTx.balanceAfter', 'Balance After')}</th>
                              <th>{t('energyCreditTx.note', 'Note')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan={9} className="border-0">
                                  <Skeleton height={15} />
                                </td>
                              </tr>
                            ) : _.isEmpty(transactions) ? (
                              <tr>
                                <td colSpan={9} className="border-0">
                                  <div className="empty-data-block">{t('energyCreditTx.noTransactions', 'No credit transactions yet')}</div>
                                </td>
                              </tr>
                            ) : (
                              _.map(transactions, (item, index) => (
                                <tr key={`enrolment-tx-${index}`}>
                                  <td>{fmtDateTime(_.get(item, 'at'))}</td>
                                  <td>{_.startCase(_.get(item, 'reason', _.get(item, 'type', '')))}</td>
                                  <td>{money(_.get(item, 'amount'))}</td>
                                  <td>{_.get(item, 'year', '—')}</td>
                                  <td>{_.get(item, 'station.name', '—')}</td>
                                  <td className="ec__id">{value(_.get(item, 'booking'))}</td>
                                  <td className="ec__id">{value(_.get(item, 'invoice_no'))}</td>
                                  <td>{money(_.get(item, 'balance_after'))}</td>
                                  <td>{value(_.get(item, 'note'))}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                      {!isLoading && !_.isEmpty(transactions) && (
                        <ReactPagination
                          currentPage={page}
                          limit={limit}
                          total={total}
                          totalPages={totalPages}
                          handlePageClick={(pageVal) => getTransactions({ page: pageVal.selected + 1 })}
                          marginPagesDisplayed={1}
                          showDataCount={true}
                        />
                      )}
                    </div>
                  </Card>
                </React.Fragment>
              )}
            </div>
          </div>
        </SkeletonTheme>
        <Footer />
      </LayoutContainer>
      {showMacModal && (
        <Modal show={showMacModal} centered className="ec__mac-modal" onHide={() => setShowMacModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>
              {_.get(detail, 'verified_evcc_id')
                ? t('enrollmentDetail.correctMacTitle', 'Correct MAC ID')
                : t('enrollmentDetail.setMacTitle', 'Record MAC ID')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="ec__mac-help mb-3">
              {_.get(detail, 'verified_evcc_id')
                ? t(
                    'enrollmentDetail.correctMacHelp',
                    'The programme follows this MAC ID. Changing it moves the credit to the new one; the amount already released is not affected.'
                  )
                : t(
                    'enrollmentDetail.setMacHelp',
                    'Use this when the customer charged but the charger never reported a MAC ID. Saving it releases the credit.'
                  )}
            </div>
            <Row className="mb-3">
              <Field label={t('enrollmentDetail.rto', 'Registration No')} md={6}>
                {value(_.get(detail, 'vehicle.rto_no') || _.get(detail, 'rto_no'))}
              </Field>
              <Field label={t('enrollmentDetail.expectedMac', 'MAC From Dealer (unverified)')} md={6}>
                {value(_.get(detail, 'expected_evcc_id'))}
              </Field>
            </Row>
            <div className="ec__field-label">{t('enrollmentDetail.onVehicle', 'MAC IDs On This Vehicle')}</div>
            <div className="ec__field-value mb-3">
              {_.isEmpty(vehicleMacs) ? t('enrollmentDetail.noMacOnVehicle', 'None recorded') : _.map(vehicleMacs, 'mac').join(', ')}
            </div>
            <TextInput
              label={t('enrollmentDetail.macId', 'MAC ID')}
              name="evcc_id"
              value={macId}
              placeholder={t('enrollmentDetail.macIdPlaceholder', 'e.g. AABB1234')}
              onChange={(e) => setMacId(_.toUpper(e.target.value))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button className="cancel--btn" onClick={() => setShowMacModal(false)}>
              {t('button.cancel', 'Cancel')}
            </Button>
            <Button
              className="create-btn"
              disabled={!_.trim(macId) || savingMac || _.trim(macId) === _.get(detail, 'verified_evcc_id')}
              onClick={saveMac}
            >
              {savingMac ? t('button.saving', 'Saving...') : t('button.save', 'Save')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default EnrollmentDetail;
